import { createFileRoute } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, FeatureGrid, CtaBand } from "@/components/MarketingShell";
import { VoiceDarCallout } from "@/components/VoiceDarCallout";

export const Route = createFileRoute("/incident-reporting")({
  component: IncidentReportingPage,
  head: () => ({
    meta: [
      { title: "Incident Reporting Software for Security Teams — MissionReady" },
      { name: "description", content: "Capture, escalate, and document security incidents from the field. Photo and video attachments, signed timestamps, follow-ups, and client-ready PDF reports." },
      ...socialMeta({
        title: "Incident Reporting Software for Security Teams — MissionReady",
        description: "Field incident capture with photo/video, signed timestamps, and client-ready PDF reports.",
        url: "https://missionready.dev/incident-reporting",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/incident-reporting" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Incident Reporting",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        description: "Capture, escalate, and document security incidents from the field with photo/video evidence, signed timestamps, and branded client PDF reports.",
        url: "https://missionready.dev/incident-reporting",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Structured incident templates",
          "Voice DAR situational logs with retained audio",
          "Photo and video evidence capture",
          "Severity-based escalation",
          "Follow-ups and revision history",
          "AI report assistance",
          "Branded client PDF exports",
        ],
      }),
    }],
  }),
});

const FEATURES = [
  { icon: "📝", title: "Structured incident types", desc: "Pre-built templates for theft, trespass, medical, fire, suspicious activity — or build your own." },
  { icon: "🎤", title: "Voice DAR™ situational logs", desc: "Hands free, eyes stay observing. Record incident and activity logs by voice; MissionReady transcribes and attaches the original audio." },
  { icon: "📸", title: "Photo & video evidence", desc: "Attach media directly from the field. Captures GPS, timestamp, and officer identity on every file." },
  { icon: "🚨", title: "Severity-based escalation", desc: "Critical incidents auto-notify supervisors and on-call dispatch. No more missed reports." },
  { icon: "🔁", title: "Follow-ups & revisions", desc: "Reopen incidents, attach updates, and keep a clean audit trail of every change with full version history." },
  { icon: "🤖", title: "AI report assistance", desc: "Optional AI helps officers write clearer, more complete narratives — without inventing facts." },
  { icon: "📤", title: "Branded client PDFs", desc: "Export polished incident reports with your logo, ready for clients, insurance, or law enforcement." },
];

function IncidentReportingPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Incident Reporting"
        title={<>Field reports your clients <span className="text-primary">actually trust.</span></>}
        subtitle="Capture incidents from any phone, escalate severity automatically, and deliver branded PDF reports without leaving the platform."
      />
      <FeatureGrid items={FEATURES} heading="Incident reporting capabilities" />
      <VoiceDarCallout context="Record incident and activity logs by voice while the scene is still in front of you. MissionReady transcribes the log, attaches the original audio, and files it alongside your incident record." />
      <CtaBand title="Tighten your incident response" subtitle="Start free for 30 days. Set up your first site in minutes." />
    </MarketingShell>
  );
}
